var _excluded = ["children", "anchorRef", "triggerRef", "trigger", "openDelay", "closeDelay", "content", "disabled", "wrapChildren", "wrapperClass"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import PropTypes from "prop-types";
import React, { useEffect, useRef } from "react";
import { ATooltip } from "../ATooltip";
import useToggle from "../../hooks/useToggle/useToggle";
import useOutsideClick from "../../hooks/useOutsideClick/useOutsideClick";
import { handleMultipleRefs } from "../../utils/helpers";
var ATriggerTooltip = function ATriggerTooltip(_ref) {
  var children = _ref.children,
    anchorRef = _ref.anchorRef,
    triggerRef = _ref.triggerRef,
    _ref$trigger = _ref.trigger,
    trigger = _ref$trigger === void 0 ? "hover" : _ref$trigger,
    _ref$openDelay = _ref.openDelay,
    openDelay = _ref$openDelay === void 0 ? 400 : _ref$openDelay,
    closeDelay = _ref.closeDelay,
    content = _ref.content,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$wrapChildren = _ref.wrapChildren,
    wrapChildren = _ref$wrapChildren === void 0 ? false : _ref$wrapChildren,
    wrapperClass = _ref.wrapperClass,
    rest = _objectWithoutProperties(_ref, _excluded);
  var childrenRef = useRef([]);
  var _useToggle = useToggle(openDelay, closeDelay),
    isOpen = _useToggle.isOpen,
    open = _useToggle.open,
    close = _useToggle.close,
    toggle = _useToggle.toggle;
  useOutsideClick({
    isEnabled: triggerRef && triggerRef.current && trigger === "click",
    rootRef: triggerRef,
    onClick: close
  });
  var childrenRefCurrent = childrenRef.current;
  useEffect(function () {
    if (!content || disabled) {
      return;
    }
    var childRefs = triggerRef && triggerRef.current ? [triggerRef.current] : childrenRefCurrent;
    switch (trigger) {
      case "hover":
        childRefs.forEach(function (childRef) {
          childRef.addEventListener("mouseenter", open);
          childRef.addEventListener("mouseleave", close);
        });
        break;
      case "click":
        childRefs.forEach(function (childRef) {
          childRef.addEventListener("click", toggle);
        });
        break;
    }
    return function () {
      childRefs.forEach(function (childRef) {
        if (!childRef) {
          return;
        }
        childRef.removeEventListener("mouseenter", open);
        childRef.removeEventListener("mouseleave", close);
        childRef.removeEventListener("click", toggle);
      });
    };
  }, [trigger, childrenRefCurrent, triggerRef, close, open, toggle, disabled, content]);

  // If an anchorRef is provided, use it. Otherwise, attach to first child.
  var tooltipAnchorRef = anchorRef || {
    current: childrenRef.current[0]
  };
  var tooltipElement = /*#__PURE__*/React.createElement(ATooltip, _extends({
    anchorRef: tooltipAnchorRef,
    open: isOpen,
    onClose: close,
    pointer: true
  }, rest), content);
  return /*#__PURE__*/React.createElement(React.Fragment, null, React.Children.map(children, function (child, index) {
    var toClone = wrapChildren ? /*#__PURE__*/React.createElement("div", {
      className: wrapperClass,
      style: {
        height: "fit-content",
        width: "fit-content"
      }
    }, child) : child;
    return /*#__PURE__*/React.cloneElement(toClone, _objectSpread(_objectSpread({}, toClone.props), {}, {
      ref: handleMultipleRefs(toClone.props.ref, function (node) {
        childrenRef.current[index] = node;
      })
    }));
  }), tooltipElement);
};
ATriggerTooltip.propTypes = {
  /** DOM event to trigger the tooltip */
  trigger: PropTypes.oneOf(["hover", "click"]),
  /**
   * Anchors the tooltip to the specified ref, leaving
   * trigger event on the child(ren).
   */
  anchorRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]),
  /** Delay in milliseconds before tooltip will open */
  openDelay: PropTypes.number,
  /** Delay in milliseconds before tooltip will close */
  closeDelay: PropTypes.number,
  /** Tooltip content */
  content: PropTypes.node,
  /** Disable the tooltip */
  disabled: PropTypes.bool,
  /**
   * Wrap the children elements in a div to allow tooltips on
   * disabled elements.
   */
  wrapChildren: PropTypes.bool,
  /**
   * Pass a class to the child wrapper.
   */
  wrapperClass: PropTypes.string
};
ATriggerTooltip.displayName = "ATriggerTooltip";
export default ATriggerTooltip;